// /src/pages/dashboard/accounts/Accounts.js  (v2.0 – modernized UI)
import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import AccountList            from './AccountList'
import * as dropdowns         from './dropdowns'
import { useAccountHandlers } from './handlers/useAccountHandlers'

/* core modals */
import AddAccountModal  from './AddAccountModal'
import UpgradeModal     from './UpgradeModal'

/* network-specific modal wrappers */
import FacebookModals   from './modals/FacebookModals'
import InstagramModals  from './modals/InstagramModals'
import ThreadsModals    from './modals/ThreadsModals'
import XModals          from './modals/XModals'
import LinkedInModals   from './modals/LinkedInModals'
import TikTokModals     from './modals/TikTokModals'
import PinterestModals  from './modals/PinterestModals'
import YouTubeModals    from './modals/YouTubeModals'

export default function Accounts () {
  const { state, ...handlers } = useAccountHandlers()

  /* ── Auto-open FB page picker after OAuth ── */
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('fb_connected') === '1') {
      handlers.setShowFbPagesModal(true)
      params.delete('fb_connected')
      navigate({ search: params.toString() }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  /* map every platform → its dropdown render fn */
  const dropdownRenders = {
    facebook : () => dropdowns.renderFacebookOptions(handlers),
    instagram: () => dropdowns.renderInstagramOptions(handlers),
    threads  : () => dropdowns.renderThreadsOptions(handlers),
    x        : () => dropdowns.renderXOptions(handlers),
    linkedin : () => dropdowns.renderLinkedInOptions(handlers),
    tiktok   : () => dropdowns.renderTikTokOptions(handlers),
    pinterest: () => dropdowns.renderPinterestOptions(handlers),
    youtube  : () => dropdowns.renderYouTubeOptions(handlers),
  }

  return (
    <div>
      {/* Page header */}
      <div style={{
        display        : 'flex',
        alignItems     : 'flex-start',
        justifyContent : 'space-between',
        marginBottom   : 24,
        flexWrap       : 'wrap',
        gap            : 12,
      }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 4 }}>
            Connected Accounts
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
            Manage your connected social media profiles and pages.
          </p>
        </div>

        {/* Add account button */}
        <button
          title={state.isAtLimit ? 'Upgrade to add more accounts' : 'Connect a new account'}
          onClick={() => {
            if (state.isAtLimit) { handlers.setShowUpgradeModal(true); return }
            handlers.setShowModal(true)
          }}
          style={{
            display     : 'flex',
            alignItems  : 'center',
            gap         : 7,
            padding     : '10px 18px',
            borderRadius: 9,
            border      : 'none',
            background  : state.isAtLimit ? '#f3f4f6' : '#2563eb',
            color       : state.isAtLimit ? '#9ca3af' : '#ffffff',
            fontSize    : 13,
            fontWeight  : 700,
            cursor      : 'pointer',
            transition  : 'background 0.15s',
            whiteSpace  : 'nowrap',
          }}
          onMouseEnter={e => {
            if (!state.isAtLimit) e.currentTarget.style.background = '#1d4ed8'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = state.isAtLimit ? '#f3f4f6' : '#2563eb'
          }}
        >
          <i className='fas fa-plus' style={{ fontSize: 11 }} />
          Connect Account
        </button>
      </div>

      {/* Loading state */}
      {state.loading && (
        <div style={{
          display        : 'flex',
          alignItems     : 'center',
          justifyContent : 'center',
          padding        : '60px 0',
          color          : '#9ca3af',
          fontSize       : 14,
          gap            : 10,
        }}>
          <div className='spinner-border spinner-border-sm' style={{ color: '#d1d5db' }} />
          Loading accounts…
        </div>
      )}

      {/* Account list */}
      {!state.loading && (
        <AccountList
          accounts={state.accounts}
          dropdowns={dropdownRenders}
          onReconnect={handlers.handleConnectPlatform}
        />
      )}

      {/* Empty state */}
      {!state.loading && state.accounts?.length === 0 && (
        <div style={{
          textAlign   : 'center',
          padding     : '60px 24px',
          background  : '#ffffff',
          border      : '1px solid #e5e7eb',
          borderRadius: 12,
          boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <div style={{
            width        : 56,
            height       : 56,
            borderRadius : '50%',
            background   : '#eff6ff',
            display      : 'flex',
            alignItems   : 'center',
            justifyContent: 'center',
            margin       : '0 auto 16px',
          }}>
            <i className='fas fa-plug' style={{ fontSize: 22, color: '#2563eb' }} />
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            No accounts connected yet
          </h3>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
            Connect your first social media account to start scheduling posts.
          </p>
          <button
            onClick={() => handlers.setShowModal(true)}
            style={{
              padding     : '10px 20px',
              borderRadius: 9,
              border      : 'none',
              background  : '#2563eb',
              color       : '#fff',
              fontSize    : 13,
              fontWeight  : 700,
              cursor      : 'pointer',
            }}
          >
            <i className='fas fa-plus me-2' style={{ fontSize: 11 }} />
            Connect Your First Account
          </button>
        </div>
      )}

      {/* Basic modals */}
      <AddAccountModal
        show={state.showModal}
        onHide={() => handlers.setShowModal(false)}
        handleConnectPlatform={handlers.handleConnectPlatform}
      />
      <UpgradeModal
        show={state.showUpgradeModal}
        onHide={() => handlers.setShowUpgradeModal(false)}
      />

      {/* Platform-specific modals */}
      <FacebookModals   {...handlers} {...state} />
      <InstagramModals  {...handlers} {...state} />
      <ThreadsModals    {...handlers} {...state} />
      <XModals          {...handlers} {...state} />
      <LinkedInModals   {...handlers} {...state} />
      <TikTokModals     {...handlers} {...state} />
      <PinterestModals  {...handlers} {...state} />
      <YouTubeModals    {...handlers} {...state} />
    </div>
  )
}